import React from 'react'
import { MultiSelectTable } from 'components/Table/MultiSelectTable/MultiSelectTable'
import { useDispatch, useSelector } from 'react-redux'
import {
  addSelectedItem,
  deselectSelectedItemById,
  selectDirectoryItems,
  selectSelectedItems,
  updateItemFieldById,
  setModified,
} from 'store/directorySlice'
import DirectoryWrapper from '../DirectoryWrapper'
import { columns } from './tableConfig'
import { definitions } from 'generated/apiTypes'
import { getHandbookName } from 'pages/MainPage/Directories/directories'
import { generateUID } from '../../../utils'
import { setBlockingWindow } from 'store/commonSlice'
import useGetPipeTypes from './hooks/useGetPipeTypes'
import useCreatePipeTypesVersion from './hooks/useCreatePipeTypesVersion'
import { HandbookPipeType, IErrorParams } from 'store/types'
import './DirectoryPipeTypes.scss'

interface IDirectoryPipeTypesProps {
  directoryKey: keyof definitions['Handbooks']
}

const newItemProperties = {
  created: true,
  modified: false,
  diameter__mm: 1420,
  thickness__mm: 1,
  roughness__mm: 1,
  pressure__MPa: 1,
  steel_name: 'K 65',
}

const DirectoryPipeTypes: React.FC<IDirectoryPipeTypesProps> = ({ directoryKey }) => {
  const dispatch = useDispatch()
  const selectedItems = useSelector(selectSelectedItems)
  const directoryItems = useSelector(selectDirectoryItems)
  const [editMode, setEditMode] = React.useState<boolean>(false)
  const [requestData, setRequestData] = React.useState<boolean>(false)
  const [saveData, setSaveData] = React.useState<boolean>()
  const [isModified, setIsModified] = React.useState<boolean>(false)
  const [newItem, setNewItem] = React.useState<HandbookPipeType>({
    id: '',
    name: '',
    ...newItemProperties,
  })

  useGetPipeTypes(requestData)
  const saveResult = useCreatePipeTypesVersion(saveData)

  React.useEffect(() => {
    setNewItem({ id: generateUID(), name: `PIPE ${directoryItems.length + 1}`, ...newItemProperties })
  }, [directoryItems.length])

  React.useEffect(() => {
    if (saveResult) {
      setIsModified(false)
      setEditMode(false)
    }
  }, [saveResult])

  const onItemSelect = (id: string) => {
    const itemToAdd = directoryItems?.find((item) => item.id === id)
    if (itemToAdd) dispatch(addSelectedItem(itemToAdd))
  }

  const onItemDeselect = (id: string) => {
    dispatch(deselectSelectedItemById(id))
  }

  const onSaveItemField = async (id: string, value: string | number, fieldName: string) => {
    const uniqueFiled = 'name'
    if (
      fieldName === uniqueFiled &&
      (directoryItems as definitions['HandbookPipeType'][])?.some((item) => item[uniqueFiled] === value)
    ) {
      dispatch(
        setBlockingWindow({
          type: 'ERROR',
          params: {
            title: 'Ошибка заполнения',
            message: 'Значение поля “Название” должно быть уникальным',
          } as IErrorParams,
        }),
      )
      const oldItem = (directoryItems as definitions['HandbookPipeType'][]).find((item) => item.id === id)
      if (oldItem) {
        await dispatch(updateItemFieldById({ id, value, fieldName }))
        dispatch(updateItemFieldById({ id, value: oldItem[uniqueFiled], fieldName }))
      }
    } else {
      dispatch(updateItemFieldById({ id, value, fieldName }))
      dispatch(setModified(id))
      setIsModified(true)
    }
  }

  const onDiscardChanges = () => {
    setRequestData(!requestData)
    setEditMode(false)
    setIsModified(false)
  }

  const onSaveChanges = () => {
    setSaveData(!saveData)
  }

  return (
    <DirectoryWrapper
      editMode={editMode}
      setEditMode={setEditMode}
      onDiscardChanges={onDiscardChanges}
      onSaveChanges={onSaveChanges}
      title={getHandbookName(directoryKey)}
      isModified={isModified}
      setIsModified={setIsModified}
      newItemToAdd={newItem as definitions['HandbookPipeType']}
    >
      <div className={'directory__content'}>
        <MultiSelectTable
          selectedItems={selectedItems}
          columns={columns}
          editMode={editMode}
          dataSource={directoryItems}
          onItemSelect={onItemSelect}
          onItemDeselect={onItemDeselect}
          setValue={onSaveItemField}
          className={'directory-table__container pipe-type-table__container'}
        />
      </div>
    </DirectoryWrapper>
  )
}

export default DirectoryPipeTypes
